import { ScrollVelocity } from "@/components/ui/scroll-velocity"
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel"
import { useIsMobile } from "@/hooks/use-mobile"
import { useEffect, useState, useRef, useCallback, useMemo } from "react"
import { GALLERY_ROW_1, GALLERY_ROW_2, getMobileUrl, PHOTOS } from "@/lib/photos"
import { useInView } from "framer-motion"

/** Descriptive alt text mapped by original (desktop) photo URL */
const PHOTO_ALT_TEXT: Record<string, string> = {
  [PHOTOS.STAGE.bandDramaticLights]: "Band performing under dramatic stage lights at The Roostertail",
  [PHOTOS.CROWD.audienceHandsRaised]: "Yacht Rock Detroit crowd with hands raised cheering at sunset",
  [PHOTOS.STAGE.nighttimeOrangeLights]: "Nighttime performance with orange stage lights and crowd",
  [PHOTOS.DJ.behindWithCrowd]: "DJ performing with dancing crowd behind",
  [PHOTOS.CROWD.sunsetStageView]: "Sunset view of the stage and audience at Yacht Rock Detroit",
  [PHOTOS.STAGE.fullBandCaptainHats]: "Band performing in captain hats at Yacht Rock Detroit",
  [PHOTOS.DANCING.crowdHandsUpCaptainHats]: "Crowd cheering with hands up and captain hats",
  [PHOTOS.STAGE.bandSpotlightBeams]: "Live band performing on outdoor stage at Yacht Rock Detroit",
  [PHOTOS.CROWD.nightStageView]: "Night stage view with audience at Yacht Rock Detroit",
  [PHOTOS.STAGE.nighttimePurpleLights]: "Stage with purple lights and crowd at Yacht Rock Detroit",
  [PHOTOS.DANCING.womenCheering]: "Friends cheering in the crowd",
  [PHOTOS.STAGE.bandLargeOutdoor]: "Live band performing on outdoor stage at Yacht Rock Detroit",
  [PHOTOS.DJ.pioneerTurntables]: "DJ performing at Pioneer turntables",
  [PHOTOS.STAGE.pianistSparkle]: "Pianist performing with sparkle backdrop",
  [PHOTOS.DANCING.crowdCheeringCaptainHat]: "Crowd cheering with hands up and captain hats",
  [PHOTOS.DJ.djKeo]: "DJ Keo performing in captain hat at Yacht Rock Detroit",
  [PHOTOS.STAGE.hostsPerformersFistBump]: "Two performers fist bumping on stage",
  [PHOTOS.DJ.nicNasty]: "DJ Nic Nasty at turntables",
  [PHOTOS.DANCING.womanWhiteDress]: "Woman in white dress dancing at Yacht Rock Detroit",
  [PHOTOS.DANCING.coupleSlowDancing]: "Couple slow dancing at Yacht Rock Detroit",
  [PHOTOS.GROUP.fiveFriendsRoostertail]: "Friends with drinks at Yacht Rock Detroit",
  [PHOTOS.FEMALE_ONLY.fourWomenRoostertail]: "Yacht Rock Detroit event photo at The Roostertail",
  [PHOTOS.COUPLE.withDrinksAtNight]: "Couple with drinks at night",
  [PHOTOS.GROUP.fourFriendsDrinks]: "Friends with drinks at Yacht Rock Detroit",
  [PHOTOS.FEMALE_ONLY.fourWomenNight]: "Yacht Rock Detroit event photo at The Roostertail",
  [PHOTOS.BOAT.groupSixPeopleWaving]: "Friends waving from a boat at The Roostertail",
  [PHOTOS.GROUP.eightPeopleGazebo]: "Friends gathered under gazebo by the water at The Roostertail",
  [PHOTOS.TWO_GUYS.hawaiianShirtsSmiling]: "Yacht Rock Detroit event photo at The Roostertail",
  [PHOTOS.FEMALE_ONLY.threeWomenDanceFloor]: "Yacht Rock Detroit event photo at The Roostertail",
  [PHOTOS.COUPLE.posingOnDock]: "Couple posing on dock by marina",
  [PHOTOS.GROUP.eightPeopleRoostertailSign]: "Group photo at The Roostertail sign",
  [PHOTOS.BAR.threePeopleCaptainOutfits]: "Yacht Rock Detroit event photo at The Roostertail",
  [PHOTOS.FEMALE_ONLY.twoWomenCaptainHats]: "Yacht Rock Detroit event photo at The Roostertail",
  [PHOTOS.THREE_MEN.captainHatsCigars]: "Yacht Rock Detroit event photo at The Roostertail",
  [PHOTOS.GROUP.fourPeopleHedge]: "Yacht Rock Detroit event photo at The Roostertail",
  [PHOTOS.FEMALE_ONLY.twoWomenDrinks]: "Yacht Rock Detroit event photo at The Roostertail",
  [PHOTOS.BOAT.groupSixPeoplePosing]: "Friends posing on a boat at Yacht Rock Detroit",
  [PHOTOS.TWO_GUYS.hawaiianShirtsBlueHour]: "Yacht Rock Detroit event photo at The Roostertail",
  [PHOTOS.GROUP.threeFriendsSign]: "Yacht Rock Detroit event photo at The Roostertail",
  [PHOTOS.FEMALE_ONLY.twoWomenPinkHair]: "Yacht Rock Detroit event photo at The Roostertail",
}

const getAltText = (url: string): string =>
  PHOTO_ALT_TEXT[url] || "Yacht Rock Detroit event photo at The Roostertail"

// Prepare row data with mobile URLs for smaller file sizes
const row1Images = GALLERY_ROW_1.map((url, i) => ({
  id: `row1-${i}`,
  src: getMobileUrl(url),
  alt: getAltText(url),
}))

const row2Images = GALLERY_ROW_2.map((url, i) => ({
  id: `row2-${i}`,
  src: getMobileUrl(url),
  alt: getAltText(url),
}))

// Duplicate images 3x for seamless infinite scroll (wrap needs buffer)
const row1Duplicated = [...row1Images, ...row1Images]
const row2Duplicated = [...row2Images, ...row2Images]

// Auto-scrolling carousel hook - pauses when not visible
const useAutoScroll = (api: CarouselApi | undefined, direction: 1 | -1 = 1, speed: number = 2000, isVisible: boolean = true) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const [isHovered, setIsHovered] = useState(false)

  const startAutoScroll = useCallback(() => {
    if (!api || isHovered || !isVisible) return
    
    intervalRef.current = setInterval(() => {
      if (direction === 1) {
        api.scrollNext()
      } else {
        api.scrollPrev()
      }
    }, speed)
  }, [api, direction, speed, isHovered, isVisible])

  const stopAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  useEffect(() => {
    if (isVisible && !isHovered) {
      startAutoScroll()
    } else {
      stopAutoScroll()
    }
    return () => stopAutoScroll()
  }, [isVisible, isHovered, startAutoScroll, stopAutoScroll])

  return { setIsHovered }
}

interface MobileCarouselProps {
  images: typeof row1Images
  direction: 1 | -1
  isVisible: boolean
}

const MobileCarousel = ({ images, direction, isVisible }: MobileCarouselProps) => {
  const [api, setApi] = useState<CarouselApi>()
  const { setIsHovered } = useAutoScroll(api, direction, 2500, isVisible)

  return (
    <Carousel
      setApi={setApi}
      opts={{
        align: "start",
        loop: true,
        dragFree: true,
      }}
      className="w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => setTimeout(() => setIsHovered(false), 3000)}
    >
      <CarouselContent className="-ml-2">
        {images.map(({ id, src, alt }) => (
          <CarouselItem key={id} className="pl-2 basis-3/4">
            <div className="relative h-40 overflow-hidden rounded-lg">
              <img
                src={src}
                alt={alt}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  )
}

const ScrollVelocitySection = () => {
  const isMobile = useIsMobile()
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { margin: "100px" })

  // Memoize row configs to prevent re-renders
  const rows = useMemo(() => [
    { images: row1Duplicated, velocity: 3 },
    { images: row2Duplicated, velocity: -3 },
  ], [])

  return (
    <section ref={sectionRef} className="py-20 md:py-32 bg-yacht-cream overflow-hidden">
      <div className="space-y-4">
        {/* Scrolling text header */}
        <ScrollVelocity velocity={-3} className="py-3 text-xl md:text-2xl font-bold tracking-tight text-yacht-charcoal uppercase">
          SONGS YOU KNOW BY HEART • DRINKS ON THE WATER • FRIENDS BY YOUR SIDE • SUNSETS THAT HIT DIFFERENT • DANCING TILL DARK • A WEEKEND YOU'LL REMEMBER •
        </ScrollVelocity>
        
        {isMobile ? (
          /* Mobile: Auto-scrolling swipeable carousels */
          <div className="space-y-4">
            <MobileCarousel images={row1Images} direction={1} isVisible={isInView} />
            <MobileCarousel images={row2Images} direction={-1} isVisible={isInView} />
          </div>
        ) : (
          /* Desktop: Scroll velocity animation with different images per row */
          rows.map((row, rowIndex) => (
            <ScrollVelocity key={rowIndex} velocity={row.velocity} className="py-2">
              {row.images.map(({ id, src, alt }, imgIndex) => (
                <div
                  key={`${id}-${imgIndex}`}
                  className="relative h-40 w-64 flex-shrink-0 overflow-hidden rounded-lg"
                >
                  <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    width={256}
                    height={160}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </ScrollVelocity>
          ))
        )}
      </div>
    </section>
  )
}

export default ScrollVelocitySection
